// The shared 360° walk-through: our own viewer, so the sales manager can walk and turn naturally and the customer's
// screen moves and turns with them (Ravi, 19 Sep: option A). A viewpoint is a 2:1 equirectangular panorama; its
// arrows lead to the next viewpoints; the unit's floor plan is the mini-map. What travels between the two screens is
// the same small state as the rest of the canvas: which viewpoint (room), which way (yaw) and how high (pitch), in
// degrees. Drawn with plain WebGL — one triangle and one texture — so it adds nothing to download.

const PI = Math.PI, RAD = PI / 180;
const clamp = (x, a, b) => Math.max(a, Math.min(b, x));
const wrap = (d) => ((((d + 180) % 360) + 360) % 360) - 180;

// ------------------------------------------------------------------------------------------------ the maths --
// A panorama's (u, v) — 0..1 across and down the image — as a direction: u 0.5 is straight ahead at yaw 0, v 0.5 the
// horizon. Looking: yaw turns right, pitch looks up.
export function dirFromUV(u, v) {
  const lon = (u - 0.5) * 2 * PI, lat = (0.5 - v) * PI;
  return [Math.cos(lat) * Math.sin(lon), Math.sin(lat), -Math.cos(lat) * Math.cos(lon)];
}
export function uvFromDir([x, y, z]) {
  const lon = Math.atan2(x, -z), lat = Math.asin(clamp(y, -1, 1));
  return { u: lon / (2 * PI) + 0.5, v: 0.5 - lat / PI };
}
function toCamera([x, y, z], yaw, pitch) {
  const cy = Math.cos(yaw * RAD), sy = Math.sin(yaw * RAD), cp = Math.cos(pitch * RAD), sp = Math.sin(pitch * RAD);
  const x1 = x * cy + z * sy, z1 = -x * sy + z * cy;          // undo the yaw
  return [x1, y * cp + z1 * sp, -y * sp + z1 * cp];            // then the pitch
}
function fromCamera([x, y, z], yaw, pitch) {
  const cy = Math.cos(yaw * RAD), sy = Math.sin(yaw * RAD), cp = Math.cos(pitch * RAD), sp = Math.sin(pitch * RAD);
  const y1 = y * cp - z * sp, z1 = y * sp + z * cp;            // the pitch
  return [x * cy - z1 * sy, y1, x * sy + z1 * cy];            // then the yaw
}
// The vertical field of view: wide on a laptop, taller on a phone held upright.
export const fovFor = (aspect) => (aspect >= 1 ? 2 * Math.atan(Math.tan(50 * RAD) / aspect) / RAD : 90);
// Where a panorama point lands on the screen (0..1 across and down), or null when it is behind or off screen.
export function project(u, v, view, aspect) {
  const c = toCamera(dirFromUV(u, v), view.yaw, view.pitch);
  if (c[2] >= -0.05) return null;
  const t = Math.tan((view.fov ?? fovFor(aspect)) * RAD / 2);
  const x = c[0] / -c[2] / (t * aspect), y = c[1] / -c[2] / t;
  if (Math.abs(x) > 1.2 || Math.abs(y) > 1.2) return null;
  return { x: (x + 1) / 2, y: (1 - y) / 2 };
}
// The panorama point under a screen point (0..1 across and down).
export function unproject(sx, sy, view, aspect) {
  const t = Math.tan((view.fov ?? fovFor(aspect)) * RAD / 2);
  const d = [(sx * 2 - 1) * t * aspect, (1 - sy * 2) * t, -1];
  const n = Math.hypot(...d);
  return uvFromDir(fromCamera(d.map((q) => q / n), view.yaw, view.pitch));
}

// ------------------------------------------------------------------------------------------------ the viewer --
const VS = "attribute vec2 p; varying vec2 q; void main(){ q = p; gl_Position = vec4(p, 0.0, 1.0); }";
const FS = `precision highp float;
uniform sampler2D tex; uniform float yaw, pitch, t, aspect; varying vec2 q;
void main(){
  vec3 d = normalize(vec3(q.x * t * aspect, q.y * t, -1.0));
  float cp = cos(pitch), sp = sin(pitch), cy = cos(yaw), sy = sin(yaw);
  d = vec3(d.x, d.y * cp - d.z * sp, d.y * sp + d.z * cp);
  d = vec3(d.x * cy - d.z * sy, d.y, d.x * sy + d.z * cy);
  float lon = atan(d.x, -d.z), lat = asin(clamp(d.y, -1.0, 1.0));
  gl_FragColor = texture2D(tex, vec2(lon / 6.2831853 + 0.5, 0.5 - lat / 3.1415927));
}`;

const live = new Set();
export const panoIn = (root) => root?.querySelector?.(".pano360")?._pano || null;

// el: the scene's .pano360 box. data: the imported walk-through (frames, plan). base: the canvas's folder.
// opts.who "nav" | "cust"; opts.hostFirst for the pointer; callbacks onLook(view, ended), onGo(frame), onPoint(uv|null),
// onHold(uv|null) — a press and hold points instead of turning (W3). show() takes a zoom: a closer look, 1–3×.
export function mountPano(el, data, base, opts = {}) {
  for (const p of [...live]) if (!p.el.isConnected) p.destroy();
  if (el._pano) return el._pano;
  const gl_ = document.createElement("canvas");
  gl_.className = "pgl";
  const spots = document.createElement("div");
  spots.className = "pano-spots";
  const ptr = document.createElement("div");
  ptr.className = "pano-ptr";
  ptr.hidden = true;
  ptr.innerHTML = "<i></i><span></span>";
  ptr.querySelector("span").textContent = opts.hostFirst || "";
  const where = document.createElement("div");
  where.className = "pano-where";
  const mini = document.createElement("div");
  mini.className = "pano-mini";
  const note = document.createElement("div");
  note.className = "pano-note";
  const peersBox = document.createElement("div");
  peersBox.className = "pano-peers";
  el.replaceChildren(gl_, spots, ptr, peersBox, where, mini, note);

  const gl = gl_.getContext("webgl", { antialias: false, alpha: false, preserveDrawingBuffer: true, powerPreference: "high-performance" });
  const state = { frame: null, prev: null, yaw: 0, pitch: 0, zoom: 1, target: { yaw: 0, pitch: 0 }, ptr: null, peers: [], drag: null, hold: null, tex: new Map(), raf: 0, sharpTimer: 0, lost: false, broken: false };
  const self = { el, show, pointer, peers, destroy, get view() { return { frame: state.frame, yaw: state.yaw, pitch: state.pitch, zoom: state.zoom }; },
    get textures() { return [...state.tex.values()].filter((t) => t.tex).length; } };
  el._pano = self;
  el.dataset.frame = "";
  live.add(self);

  // The sharp tier: 4096 px on a laptop, 2048 px on a phone or a small GPU — a 4096 panorama is ~32 MB of graphics
  // memory, and a phone holding many of them kills the tab (audit F10).
  const maxTex = gl ? gl.getParameter(gl.MAX_TEXTURE_SIZE) : 0;
  const small = (navigator.deviceMemory && navigator.deviceMemory <= 4) || maxTex < 4096
    || (window.matchMedia?.("(pointer: coarse)").matches && Math.min(screen.width, screen.height) < 820);
  const SHARP = small ? "md" : "img";

  let prog, uni = {};
  function setup() {
    const sh = (type, src) => { const x = gl.createShader(type); gl.shaderSource(x, src); gl.compileShader(x); return x; };
    prog = gl.createProgram();
    gl.attachShader(prog, sh(gl.VERTEX_SHADER, VS));
    gl.attachShader(prog, sh(gl.FRAGMENT_SHADER, FS));
    gl.linkProgram(prog);
    if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) { state.broken = true; note.textContent = "This phone cannot draw the 360° view."; return; }
    gl.useProgram(prog);
    const buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
    const loc = gl.getAttribLocation(prog, "p");
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
    for (const n of ["tex", "yaw", "pitch", "t", "aspect"]) uni[n] = gl.getUniformLocation(prog, n);
  }
  if (gl) setup();
  else { state.broken = true; note.textContent = "This browser cannot draw the 360° view."; }
  // The phone can take the graphics back (switching to WhatsApp, low memory): say so, and rebuild from the pictures
  // already downloaded when it is given back (audit F10).
  gl_.addEventListener("webglcontextlost", (e) => { e.preventDefault(); state.lost = true; note.textContent = "Reloading the view…"; for (const t of state.tex.values()) t.tex = null; });
  gl_.addEventListener("webglcontextrestored", () => {
    state.lost = false;
    setup();
    for (const t of state.tex.values()) if (t.ready) upload(t);
    draw();
  });

  // --- images: the small one at once, the sharp one after a moment on the viewpoint; the next rooms' small ones ahead
  function upload(entry) {
    if (!gl || state.lost || state.broken || !entry.img.naturalWidth) return;
    entry.tex = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, entry.tex);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGB, gl.RGB, gl.UNSIGNED_BYTE, entry.img);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.REPEAT);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  }
  function texture(id, which) {
    const key = `${id}:${which}`;
    if (state.tex.has(key)) return state.tex.get(key);
    const f = data.frames[id];
    const entry = { key, id, which, ready: false, tex: null, img: new Image(), tries: 0 };
    state.tex.set(key, entry);
    if (state.broken) return entry; // nothing can be drawn: nothing is downloaded
    const load = () => {
      entry.img.src = base + (which === "sm" ? f.sm : f[which] || f.img);
      // Decoded before it is handed to the GPU, so the upload does not stall the screen.
      (entry.img.decode ? entry.img.decode() : new Promise((ok, no) => { entry.img.onload = ok; entry.img.onerror = no; }))
        .then(() => {
          if (state.tex.get(key) !== entry) return; // dropped while it loaded
          upload(entry);
          entry.ready = true;
          if (id === state.frame) draw();
        })
        .catch(() => {
          if (state.tex.get(key) !== entry) return;
          if (entry.tries++ < 2) { setTimeout(load, 3000); return; }
          if (id === state.frame) note.textContent = "This view has not loaded — check the connection.";
        });
    };
    load();
    return entry;
  }
  // Only what is needed stays: this viewpoint and the one before, sharp and small; the next rooms, small.
  function prune() {
    const keep = new Set([`${state.frame}:sm`, `${state.frame}:${SHARP}`, `${state.prev}:${SHARP}`, `${state.prev}:sm`,
      ...(data.frames[state.frame]?.links || []).map((l) => `${l.to}:sm`)]);
    for (const [key, t] of state.tex) {
      if (keep.has(key)) continue;
      if (t.tex && gl) gl.deleteTexture(t.tex);
      t.img.removeAttribute("src");
      state.tex.delete(key);
    }
  }
  function current() {
    const big = state.tex.get(`${state.frame}:${SHARP}`), sm = state.tex.get(`${state.frame}:sm`);
    return big?.tex ? big : sm?.tex ? sm : null;
  }

  // --- drawing
  function size() {
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    const w = Math.max(1, Math.round(el.clientWidth * dpr)), h = Math.max(1, Math.round(el.clientHeight * dpr));
    if (gl_.width !== w || gl_.height !== h) { gl_.width = w; gl_.height = h; }
    return el.clientWidth / Math.max(1, el.clientHeight);
  }
  const fovNow = (aspect) => fovFor(aspect) / state.zoom;
  function draw() {
    const aspect = size();
    const t = Math.tan(fovNow(aspect) * RAD / 2);
    const tex = current();
    if (gl && !state.lost && !state.broken && !tex) { gl.viewport(0, 0, gl_.width, gl_.height); gl.clearColor(0.07, 0.07, 0.07, 1); gl.clear(gl.COLOR_BUFFER_BIT); }
    if (gl && tex && !state.lost && !state.broken) {
      gl.viewport(0, 0, gl_.width, gl_.height);
      gl.bindTexture(gl.TEXTURE_2D, tex.tex);
      gl.uniform1i(uni.tex, 0);
      gl.uniform1f(uni.yaw, state.yaw * RAD);
      gl.uniform1f(uni.pitch, state.pitch * RAD);
      gl.uniform1f(uni.t, t);
      gl.uniform1f(uni.aspect, aspect);
      gl.drawArrays(gl.TRIANGLES, 0, 3);
      note.textContent = "";
    }
    const view = { yaw: state.yaw, pitch: state.pitch, fov: fovNow(aspect) };
    for (const b of spots.children) {
      const at = project(+b.dataset.u, +b.dataset.v, view, aspect);
      b.hidden = !at;
      if (at) { b.style.left = `${at.x * 100}%`; b.style.top = `${at.y * 100}%`; }
    }
    const p = state.ptr && project(state.ptr.u, state.ptr.v, view, aspect);
    ptr.hidden = !p;
    if (p) { ptr.style.left = `${p.x * 100}%`; ptr.style.top = `${p.y * 100}%`; }
    // Other people's pointers in the sphere (W3): a dot and a name each, where they are looking in this viewpoint.
    const seen = new Set();
    for (const q of state.peers) {
      if (q.frame && q.frame !== state.frame) continue;
      const at = project(q.u, q.v, view, aspect);
      if (!at) continue;
      seen.add(q.id);
      let d = peersBox.querySelector(`[data-id="${CSS.escape(q.id)}"]`);
      if (!d) { d = document.createElement("div"); d.className = "ptr-peer"; d.dataset.id = q.id; d.append(document.createElement("span")); peersBox.append(d); }
      d.style.left = `${at.x * 100}%`; d.style.top = `${at.y * 100}%`;
      d.style.setProperty("--peer", q.colour);
      d.classList.toggle("gone", Boolean(q.gone));
      d.firstChild.textContent = q.name;
    }
    for (const d of [...peersBox.children]) if (!seen.has(d.dataset.id)) d.remove();
    const cone = mini.querySelector(".me b");
    if (cone) cone.style.transform = `rotate(${state.yaw}deg)`;
    el.dataset.yaw = state.yaw.toFixed(1);
    el.dataset.pitch = state.pitch.toFixed(1);
  }
  function tick() {
    state.raf = 0;
    const dy = wrap(state.target.yaw - state.yaw), dp = state.target.pitch - state.pitch;
    if (Math.abs(dy) < 0.05 && Math.abs(dp) < 0.05) { state.yaw = state.target.yaw; state.pitch = state.target.pitch; draw(); return; }
    state.yaw = wrap(state.yaw + dy * 0.22);
    state.pitch += dp * 0.22;
    draw();
    state.raf = requestAnimationFrame(tick);
  }

  // --- a viewpoint: its arrows, its place on the plan, its name
  function enter(id) {
    if (!data.frames[id]) id = data.start;
    if (state.frame && state.frame !== id) state.prev = state.frame;
    state.frame = id;
    el.dataset.frame = id;
    const f = data.frames[id];
    texture(id, "sm");
    clearTimeout(state.sharpTimer);
    state.sharpTimer = setTimeout(() => { if (state.frame === id) texture(id, SHARP); }, 600);
    for (const l of f.links) texture(l.to, "sm");
    prune();
    spots.replaceChildren(...f.links.map((l) => {
      const b = document.createElement("button");
      b.type = "button";
      b.className = "pano-go";
      b.dataset.u = l.u; b.dataset.v = l.v; b.dataset.to = l.to;
      b.innerHTML = "<i></i><span></span>";
      b.querySelector("span").textContent = l.label || data.frames[l.to]?.room || "";
      b.addEventListener("click", (e) => { e.stopPropagation(); opts.onGo?.(l.to); });
      return b;
    }));
    where.textContent = f.room || "";
    paintMini();
    draw();
  }
  function paintMini() {
    const plan = data.plan;
    if (!plan) { mini.hidden = true; return; }
    // Only the plan itself, not the blurred frame around it: the box the dots sit in, with a margin.
    const us = plan.dots.map((d) => d.u), vs = plan.dots.map((d) => d.v);
    const b = { u0: Math.max(0, Math.min(...us) - 0.07), u1: Math.min(1, Math.max(...us) + 0.07), v0: Math.max(0, Math.min(...vs) - 0.1), v1: Math.min(1, Math.max(...vs) + 0.1) };
    const sx = 1 / (b.u1 - b.u0), sy = 1 / (b.v1 - b.v0);
    mini.style.aspectRatio = `${(plan.w * (b.u1 - b.u0)) / (plan.h * (b.v1 - b.v0))}`;
    mini.style.backgroundImage = `url('${base}${plan.img}')`;
    mini.style.backgroundSize = `${sx * 100}% ${sy * 100}%`;
    mini.style.backgroundPosition = `${(b.u0 / (1 - (b.u1 - b.u0))) * 100}% ${(b.v0 / (1 - (b.v1 - b.v0))) * 100}%`;
    mini.replaceChildren(...plan.dots.map((d) => {
      const dot = document.createElement(opts.who === "nav" ? "button" : "i");
      dot.className = "dot" + (d.to === state.frame ? " me" : "");
      if (opts.who === "nav") { dot.type = "button"; dot.title = data.frames[d.to]?.room || ""; dot.addEventListener("click", () => opts.onGo?.(d.to)); }
      dot.style.left = `${(d.u - b.u0) * sx * 100}%`;
      dot.style.top = `${(d.v - b.v0) * sy * 100}%`;
      if (d.to === state.frame) dot.innerHTML = "<b></b>";
      return dot;
    }));
  }

  // show({frame, yaw, pitch}, {animate}): where to look. Following glides there; one's own drag lands at once.
  function show(view, { animate = true } = {}) {
    if (state.drag && (!view.frame || view.frame === state.frame)) return; // one's own drag is the truth while it lasts
    if (view.frame && view.frame !== state.frame) enter(view.frame);
    else if (!state.frame) enter(data.start);
    state.target = { yaw: wrap(view.yaw ?? state.target.yaw), pitch: clamp(view.pitch ?? state.target.pitch, -85, 85) };
    if (view.zoom != null) state.zoom = clamp(+view.zoom || 1, 1, 3);
    if (!animate || state.drag) { state.yaw = state.target.yaw; state.pitch = state.target.pitch; draw(); return; }
    if (!state.raf) state.raf = requestAnimationFrame(tick);
  }
  function pointer(p) {
    state.ptr = p && Number.isFinite(p.u) ? { u: p.u, v: p.v } : null;
    draw();
  }
  function peers(list) {
    state.peers = (list || []).filter((q) => Number.isFinite(q.u) && Number.isFinite(q.v));
    draw();
  }
  const uvAt = (e) => { const p = at(e); return unproject(p.x, p.y, { yaw: state.yaw, pitch: state.pitch, fov: fovNow(el.clientWidth / Math.max(1, el.clientHeight)) }, el.clientWidth / Math.max(1, el.clientHeight)); };

  // --- looking around: drag to turn, anywhere but an arrow
  const at = (e) => { const r = el.getBoundingClientRect(); return { x: (e.clientX - r.left) / r.width, y: (e.clientY - r.top) / r.height }; };
  el.addEventListener("pointerdown", (e) => {
    if (e.target.closest("button")) return;
    state.drag = { x: e.clientX, y: e.clientY, yaw: state.yaw, pitch: state.pitch, moved: false, id: e.pointerId };
    el.setPointerCapture?.(e.pointerId);
    // A press and hold points, if this screen asked for it; a drag before then turns as always.
    clearTimeout(state.holdTimer);
    if (opts.onHold) {
      const first = { clientX: e.clientX, clientY: e.clientY };
      state.holdTimer = setTimeout(() => { if (state.drag && !state.drag.moved) { state.hold = e.pointerId; opts.onHold(uvAt(first), state.frame); } }, 350);
    }
  });
  el.addEventListener("pointermove", (e) => {
    const d = state.drag;
    if (state.hold != null && state.hold === e.pointerId) { opts.onHold(uvAt(e), state.frame); e.preventDefault(); return; }
    if (d && d.id === e.pointerId) {
      const dx = e.clientX - d.x, dy = e.clientY - d.y;
      if (!d.moved && Math.hypot(dx, dy) < 5) return;
      d.moved = true;
      clearTimeout(state.holdTimer);
      const k = fovNow(el.clientWidth / Math.max(1, el.clientHeight)) / Math.max(1, el.clientHeight);
      state.target = { yaw: wrap(d.yaw - dx * k), pitch: clamp(d.pitch + dy * k, -85, 85) };
      state.yaw = state.target.yaw; state.pitch = state.target.pitch;
      draw();
      opts.onLook?.({ frame: state.frame, yaw: state.yaw, pitch: state.pitch }, false);
      e.preventDefault();
      return;
    }
    if (opts.onPoint && !d && e.pointerType === "mouse") {
      opts.onPoint(uvAt(e));
    }
  });
  const end = (e) => {
    clearTimeout(state.holdTimer);
    if (state.hold != null && state.hold === e.pointerId) { state.hold = null; state.drag = null; opts.onHold(null, state.frame); return; }
    const d = state.drag;
    if (!d || d.id !== e.pointerId) return;
    state.drag = null;
    if (d.moved) opts.onLook?.({ frame: state.frame, yaw: state.yaw, pitch: state.pitch }, true);
  };
  el.addEventListener("pointerup", end);
  el.addEventListener("pointercancel", end);
  el.addEventListener("pointerleave", () => opts.onPoint?.(null));
  const ro = new ResizeObserver(() => draw());
  ro.observe(el);

  function destroy() {
    live.delete(self);
    ro.disconnect();
    cancelAnimationFrame(state.raf);
    clearTimeout(state.sharpTimer);
    clearTimeout(state.holdTimer);
    if (gl) { for (const t of state.tex.values()) if (t.tex) gl.deleteTexture(t.tex); gl.getExtension("WEBGL_lose_context")?.loseContext(); }
    state.tex.clear();
    el._pano = null;
  }
  return self;
}
